import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft,
    CheckCircle2,
    Clock3,
    XCircle,
    X,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { sampleOrder, statusMeta } from "@/data/site";

const TABS = [
    { key: "ALL", label: "Semua" },
    { key: "DAPAT", label: "Dapat" },
    { key: "BELUM_DAPAT", label: "Belum Dapat" },
    { key: "SOLD_OUT", label: "Sold Out" },
];

const StatusBadge = ({ status }) => {
    const meta = statusMeta[status];
    const Icon =
        status === "DAPAT" ? CheckCircle2 : status === "SOLD_OUT" ? XCircle : Clock3;
    return (
        <span
            data-testid={`status-badge-${status}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide border ${meta.classes}`}
        >
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
};

export default function OrderStatus() {
    const { token } = useParams();
    const [tab, setTab] = useState("ALL");
    const [openImg, setOpenImg] = useState(null);

    // Placeholder: real fetch will use `token` against Google Sheets/AppSheet.
    const order = sampleOrder;

    const counts = useMemo(() => {
        return order.items.reduce(
            (acc, it) => {
                acc[it.status] = (acc[it.status] ?? 0) + 1;
                acc.ALL += 1;
                return acc;
            },
            { ALL: 0, DAPAT: 0, BELUM_DAPAT: 0, SOLD_OUT: 0 },
        );
    }, [order]);

    const filtered =
        tab === "ALL" ? order.items : order.items.filter((i) => i.status === tab);

    return (
        <div
            data-testid="page-order-status"
            className="wx-container py-6"
        >
            <Link
                to="/"
                data-testid="back-home"
                className="inline-flex items-center gap-2 text-sm text-[color:var(--wx-muted)] hover:text-[color:var(--wx-ink)]"
            >
                <ArrowLeft className="w-4 h-4" /> Kembali
            </Link>

            {/* Compact summary */}
            <section
                data-testid="order-summary"
                className="mt-4 wx-card p-4"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="wx-eyebrow">Pesanan Anda</p>
                        <p className="mt-1 text-lg font-semibold text-[color:var(--wx-ink)] leading-tight truncate">
                            {order.customerName}
                        </p>
                        <p className="mt-0.5 text-[11px] text-[color:var(--wx-muted)]">
                            PO {order.destination} · {order.poWindow}
                        </p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[color:var(--wx-blueSoft)] text-[color:var(--wx-blue)] px-2.5 py-1 text-[10px] font-bold tracking-wide">
                        {order.paymentStatus}
                    </span>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-[color:var(--wx-ink2)] font-semibold">
                        {counts.DAPAT} dari {counts.ALL} barang sudah dapat
                    </span>
                    <span className="font-mono text-[10px] text-[color:var(--wx-muted)] truncate ml-2">
                        {token}
                    </span>
                </div>

                <div className="mt-2 h-1.5 rounded-full bg-[color:var(--wx-surface)] overflow-hidden">
                    <div
                        className="h-full bg-[color:var(--wx-blue)] transition-all"
                        style={{
                            width: `${Math.round((counts.DAPAT / Math.max(counts.ALL, 1)) * 100)}%`,
                        }}
                    />
                </div>
            </section>

            {/* Tabs */}
            <div
                data-testid="status-tabs"
                className="mt-4 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1"
            >
                {TABS.map((t) => {
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            data-testid={`tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition ${
                                active
                                    ? "bg-[color:var(--wx-ink)] text-white border-[color:var(--wx-ink)]"
                                    : "bg-white text-[color:var(--wx-muted)] border-[color:var(--wx-line)] hover:text-[color:var(--wx-ink)]"
                            }`}
                        >
                            {t.label}
                            <span
                                className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full text-[9px] px-1 ${
                                    active
                                        ? "bg-white/20 text-white"
                                        : "bg-[color:var(--wx-blueSoft)] text-[color:var(--wx-blue)]"
                                }`}
                            >
                                {counts[t.key]}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Items */}
            <ul
                data-testid="order-items"
                className="mt-3 wx-card divide-y divide-[color:var(--wx-line)] overflow-hidden"
            >
                {filtered.map((it) => (
                    <li
                        key={it.id}
                        data-testid={`order-item-${it.id}`}
                        className="p-3 flex items-start gap-3"
                    >
                        <button
                            type="button"
                            data-testid={`thumb-${it.id}`}
                            onClick={() =>
                                setOpenImg({ src: it.image, name: it.name })
                            }
                            className="shrink-0 relative w-12 h-12 rounded-lg overflow-hidden border border-[color:var(--wx-line)] focus:outline-none focus:ring-2 focus:ring-[color:var(--wx-blue)]"
                            aria-label={`Lihat foto ${it.name}`}
                        >
                            <img
                                src={it.image}
                                alt={it.name}
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </button>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <p className="text-[13px] font-semibold text-[color:var(--wx-ink)] leading-snug line-clamp-2">
                                    {it.name}
                                </p>
                                <StatusBadge status={it.status} />
                            </div>
                            <p className="mt-0.5 text-[11px] text-[color:var(--wx-muted)] truncate">
                                {it.variant} · Qty {it.qty}
                            </p>
                            <div className="mt-1 flex items-center justify-between gap-2">
                                <p className="text-[10px] text-[color:var(--wx-muted)] truncate">
                                    {it.note}
                                </p>
                                <p
                                    data-testid={`price-${it.id}`}
                                    className="shrink-0 text-[13px] font-bold text-[color:var(--wx-ink)]"
                                >
                                    {it.price}
                                </p>
                            </div>
                        </div>
                    </li>
                ))}
                {filtered.length === 0 && (
                    <li className="p-6 text-center text-xs text-[color:var(--wx-muted)]">
                        Tidak ada barang dengan status ini.
                    </li>
                )}
            </ul>

            <p className="mt-4 text-[11px] text-[color:var(--wx-muted)]">
                Halaman ini adalah pratinjau. Data pesanan akan segera
                terhubung dengan sistem internal WxFaous.
            </p>

            {/* Lightbox */}
            <Dialog
                open={!!openImg}
                onOpenChange={(v) => !v && setOpenImg(null)}
            >
                <DialogContent
                    data-testid="lightbox"
                    className="max-w-2xl p-0 bg-transparent border-0 shadow-none [&>button]:hidden"
                >
                    <DialogTitle className="sr-only">
                        {openImg?.name ?? "Foto produk"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Foto produk WxFaous
                    </DialogDescription>
                    <div className="relative rounded-2xl overflow-hidden bg-black">
                        {openImg && (
                            <img
                                src={openImg.src}
                                alt={openImg.name}
                                className="w-full h-auto max-h-[80vh] object-contain bg-black"
                            />
                        )}
                        <button
                            type="button"
                            data-testid="lightbox-close"
                            onClick={() => setOpenImg(null)}
                            className="absolute top-3 right-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/95 text-[color:var(--wx-ink)] shadow-lg"
                            aria-label="Tutup"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        {openImg && (
                            <p className="absolute bottom-0 inset-x-0 p-3 text-xs text-white/90 bg-gradient-to-t from-black/70 to-transparent">
                                {openImg.name}
                            </p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
